import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { firstEnabledIndex, isRovingKey, resolveRovingIndex } from '../components/rovingFocus';
import { usePopoverClamp } from '../components/usePopoverClamp';

/**
 * Menu d'ACTIONS (jobs de masse ET actions de sélection), distinct du `Menu` de
 * sélection unique : les entrées déclenchent une action (pas un choix persistant).
 * Reprend les mêmes conventions d'accessibilité (rôle `menu`/`menuitem`, clavier
 * fléché, Échap, clic extérieur). Les actions « à venir » / occupées sont désactivées.
 *
 * Réutilisé par la barre de sélection (menu « Actions… ») : d'où la prise en charge
 * optionnelle d'une icône par entrée, d'un séparateur (`separatorBefore`) et d'une
 * variante `danger` (icône + texte, jamais la couleur seule), plus d'un déclencheur
 * ÉTIQUETÉ (`triggerVariant='labelled'`) — la mécanique d'accessibilité reste UNIQUE.
 * `separatorBefore` et `danger` n'ont plus d'appelant depuis que « Supprimer » a été
 * promue en bouton direct (XPHOTO-32 lot 2) ; ils restent OFFERTS par ce menu générique.
 */
export interface ToolAction {
  id: string;
  label: string;
  onSelect?: () => void;
  disabled?: boolean;
  /** Icône optionnelle (SVG Heroicon `aria-hidden`) affichée avant le libellé. */
  icon?: React.ReactNode;
  /** Entrée « danger » (ex. Supprimer) — texte en `text-danger`, sens porté aussi par l'icône. */
  danger?: boolean;
  /** Insère un séparateur visuel AVANT cette entrée (détache p. ex. Supprimer en bas). */
  separatorBefore?: boolean;
}

interface ToolsMenuProps {
  trigger: React.ReactNode;
  label: string;
  closeLabel: string;
  actions: readonly ToolAction[];
  /** Message d'erreur affiché sous les actions (p. ex. échec de scan). */
  error?: string | undefined;
  /**
   * Forme du déclencheur : `icon` (bouton carré icône seule, défaut — cluster d'en-tête)
   * ou `labelled` (bouton icône + texte visible, p. ex. « Actions… » de la barre de sélection).
   */
  triggerVariant?: 'icon' | 'labelled';
}

export function ToolsMenu({
  trigger,
  label,
  closeLabel,
  actions,
  error,
  triggerVariant = 'icon',
}: ToolsMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  // Maintien dans le cadre visible. Le décalage est calculé sur la boîte RENDUE : il
  // couvre donc les DEUX ancrages ci-dessous sans avoir à savoir lequel est actif —
  // `start-0` déborde par la droite exactement comme `end-0` déborde par la gauche.
  const panelRef = usePopoverClamp(open);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  /** Masque d'activabilité des entrées — les désactivées sont ENJAMBÉES par les flèches. */
  const enabled = useMemo(() => actions.map((a) => a.disabled !== true), [actions]);

  const openMenu = useCallback(() => {
    setActiveIndex(firstEnabledIndex(enabled));
    setOpen(true);
  }, [enabled]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, close]);

  useEffect(() => {
    if (open) itemsRef.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const onButtonKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    if (!isRovingKey(e.key, 'vertical')) return;
    e.preventDefault();
    // `null` = menu ENTIÈREMENT désactivé : on ne bouge pas (et surtout, pas de boucle).
    const next = resolveRovingIndex(e.key, activeIndex, enabled, 'vertical');
    if (next !== null) setActiveIndex(next);
  };

  const trigger_ = (action: ToolAction): void => {
    if (action.disabled) return;
    action.onSelect?.();
    close();
  };

  const labelled = triggerVariant === 'labelled';

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onButtonKeyDown}
        className={
          labelled
            ? 'flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none'
            : 'flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none'
        }
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          id={menuId}
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          // Un déclencheur étiqueté vit à gauche (barre de sélection) → aligne le menu à
          // gauche ; le déclencheur icône vit dans le cluster de droite → aligne à droite.
          className={`absolute z-20 mt-1 min-w-52 rounded-lg border border-border bg-surface p-1 shadow-lg ${labelled ? 'start-0' : 'end-0'}`}
        >
          {actions.map((action, index) => (
            <div key={action.id}>
              {action.separatorBefore && (
                <div role="separator" className="my-1 border-t border-border" />
              )}
              <button
                ref={(el) => {
                  itemsRef.current[index] = el;
                }}
                type="button"
                role="menuitem"
                disabled={action.disabled}
                tabIndex={index === activeIndex ? 0 : -1}
                onClick={() => trigger_(action)}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:text-muted disabled:opacity-60 motion-reduce:transition-none ${action.danger ? 'text-danger' : 'text-fg'}`}
              >
                {action.icon}
                <span className="flex-1">{action.label}</span>
              </button>
            </div>
          ))}
          {error && (
            <p role="alert" className="px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}
          <span className="sr-only">{closeLabel}</span>
        </div>
      )}
    </div>
  );
}
